import { useKeycloak } from "@react-keycloak/web";

/**
 * Provider-agnostic shape that the app consumes. Anything Gundi needs from the
 * identity layer goes here; today it is satisfied by Keycloak via the default
 * `useAuth` hook below, but a future provider can be swapped in without
 * touching feature code as long as it returns the same `AuthState`.
 */
export type AuthState = {
    token: string | undefined;
    authenticated: boolean;
    isTokenExpired: () => boolean;
};

// Double-gated: env flag plus non-production build, so an accidental flag in
// a prod build cannot silently treat all users as authenticated.
const MOCK_AUTH =
    process.env.REACT_APP_MOCK_USER_PROFILE === "true" &&
    process.env.NODE_ENV !== "production";

const MOCK_AUTH_STATE: AuthState = {
    token: "mock-token",
    authenticated: true,
    isTokenExpired: () => false,
};

/**
 * Returns the current auth state for the app. Wraps Keycloak today; when the
 * team migrates to another auth provider, this is the single place to swap.
 *
 * When `REACT_APP_MOCK_USER_PROFILE=true`, returns a fake authenticated state
 * so UI flows that depend on a token (e.g. the Account page) can be exercised
 * without a live identity provider.
 */
export const useAuth = (): AuthState => {
    const { keycloak } = useKeycloak();

    if (MOCK_AUTH) {
        return MOCK_AUTH_STATE;
    }

    return {
        token: keycloak.token,
        authenticated: !!keycloak.authenticated,
        isTokenExpired: () => (keycloak.isTokenExpired ? keycloak.isTokenExpired() : false),
    };
};
